import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Wifi,
  WifiOff,
  AlertTriangle,
  Package,
  HelpCircle,
  Users,
  Search,
  Link2,
  Unlink,
  DollarSign,
} from 'lucide-react';
import { AccountFilterCombobox } from '@/features/quotes/components/AccountFilterCombobox';
import { useSimCards, useSimCardsSummary } from '../hooks/useSimCards';
import { SimInventoryTable, type SortKey } from '../components/SimInventoryTable';
import { SimMatchingPanel } from '../components/SimMatchingPanel';
import { SimDetailSheet } from '../components/SimDetailSheet';
import { SimInventoryHeader } from '../components/SimInventoryHeader';
import { SimSummaryCard } from '../components/SimSummaryCard';
import { TelnyxInventory } from '../components/TelnyxInventory';
import type { SimCardStatus, SimCardFilters, SimProvider } from '../types';

/**
 * Format data size from MB to human readable format
 * @param mb - Size in megabytes
 * @returns Formatted string with appropriate unit (MB, GB, TB)
 */
function formatDataSize(mb: number): string {
  if (mb < 1) {
    // Show as KB for values less than 1 MB
    const kb = mb * 1024;
    return `${kb.toLocaleString(undefined, { maximumFractionDigits: 2 })} KB`;
  }
  if (mb < 1024) {
    return `${mb.toLocaleString(undefined, { maximumFractionDigits: 2 })} MB`;
  }
  if (mb < 1024 * 1024) {
    const gb = mb / 1024;
    return `${gb.toLocaleString(undefined, { maximumFractionDigits: 2 })} GB`;
  }
  const tb = mb / (1024 * 1024);
  return `${tb.toLocaleString(undefined, { maximumFractionDigits: 2 })} TB`;
}

function useDebounce<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
}

type MatchFilter = 'all' | 'matched' | 'unmatched';
const ALL_ACCOUNTS_VALUE = 'all';

export default function SimInventoryPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const provider: SimProvider = searchParams.get('provider') === 'telnyx' ? 'telnyx' : 'jasper';

  const handleProviderChange = (nextProvider: SimProvider) => {
    const nextParams = new URLSearchParams(searchParams);
    if (nextProvider === 'jasper') nextParams.delete('provider');
    else nextParams.set('provider', nextProvider);
    setSearchParams(nextParams, { replace: true });
  };

  return provider === 'telnyx'
    ? <TelnyxInventory onProviderChange={handleProviderChange} />
    : <JasperInventory onProviderChange={handleProviderChange} />;
}

interface JasperInventoryProps {
  onProviderChange: (provider: SimProvider) => void;
}

function JasperInventory({ onProviderChange }: JasperInventoryProps) {
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 300);
  const [status, setStatus] = useState<string>('all');
  const [selectedAccountId, setSelectedAccountId] = useState(ALL_ACCOUNTS_VALUE);
  const [showUnassigned, setShowUnassigned] = useState(false);
  const [matchFilter, setMatchFilter] = useState<MatchFilter>('all');
  const [page, setPage] = useState(0);
  const [sortKey, setSortKey] = useState<SortKey>('status');
  const [sortDirection, setSortDirection] = useState<1 | -1>(1);
  const [selectedSimId, setSelectedSimId] = useState<string | null>(null);
  const pageLength = 25;

  // Reset to first page when filters change
  useEffect(() => {
    setPage(0);
  }, [debouncedSearch, status, selectedAccountId, showUnassigned, matchFilter, sortKey, sortDirection]);

  // Handle column sorting
  const handleSort = (key: SortKey) => {
    if (key === sortKey) {
      // Toggle direction if same column
      setSortDirection((d) => (d === 1 ? -1 : 1));
    } else {
      // New column, default to ascending
      setSortKey(key);
      setSortDirection(1);
    }
  };

  const handleAccountChange = (accountId: string) => {
    setSelectedAccountId(accountId);
    if (accountId !== ALL_ACCOUNTS_VALUE) {
      setShowUnassigned(false);
    }
  };

  const toggleUnassigned = () => {
    const next = !showUnassigned;
    setShowUnassigned(next);
    if (next) {
      setSelectedAccountId(ALL_ACCOUNTS_VALUE);
    }
  };

  // Build filters
  const filters: SimCardFilters = {
    search: debouncedSearch || undefined,
    status: status !== 'all' ? (status as SimCardStatus) : undefined,
    account_id: selectedAccountId !== ALL_ACCOUNTS_VALUE ? selectedAccountId : undefined,
    unassigned: showUnassigned || undefined,
    matched: matchFilter === 'all' ? undefined : matchFilter === 'matched',
    page_number: page,
    page_length: pageLength,
    sort_key: sortKey,
    sort_direction: sortDirection,
  };

  // Queries
  const simCardsQuery = useSimCards(filters);
  const summaryQuery = useSimCardsSummary();

  // Use server-side filtered results
  const simCards = simCardsQuery.data?.sim_cards || [];
  const total = simCardsQuery.data?.total || 0;
  const summary = summaryQuery.data;
  const totalPages = Math.ceil(total / pageLength);
  const isLoading = simCardsQuery.isLoading;
  const error = simCardsQuery.error;

  const handleRefresh = () => {
    simCardsQuery.refetch();
    summaryQuery.refetch();
  };

  return (
    <div className="space-y-5">
      <SimInventoryHeader
        provider="jasper"
        onProviderChange={onProviderChange}
        onRefresh={handleRefresh}
        isRefreshing={simCardsQuery.isFetching}
      />

      {/* Matching Panel */}
      <SimMatchingPanel />

      {/* Summary Cards */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4 xl:grid-cols-7">
        <SimSummaryCard
          title="Total SIMs"
          value={summary?.total}
          icon={<Package className="h-4 w-4 text-muted-foreground" />}
          isLoading={summaryQuery.isLoading}
        />
        <SimSummaryCard
          title="Active"
          value={summary?.active}
          icon={<Wifi className="h-4 w-4 text-green-500" />}
          isLoading={summaryQuery.isLoading}
          valueClassName="text-green-600"
        />
        <SimSummaryCard
          title="Inactive"
          value={summary?.inactive}
          icon={<WifiOff className="h-4 w-4 text-gray-400" />}
          isLoading={summaryQuery.isLoading}
        />
        <SimSummaryCard
          title="Suspended"
          value={summary?.suspended}
          icon={<AlertTriangle className="h-4 w-4 text-yellow-500" />}
          isLoading={summaryQuery.isLoading}
          valueClassName="text-yellow-600"
        />
        <SimSummaryCard
          title="Unassigned"
          value={summary?.unassigned}
          icon={<Users className="h-4 w-4 text-orange-500" />}
          isLoading={summaryQuery.isLoading}
          valueClassName={summary?.unassigned ? 'text-orange-600' : undefined}
        />
        <SimSummaryCard
          title="Usage (MB)"
          value={summary?.total_usage_mb ? formatDataSize(parseFloat(summary.total_usage_mb)) : '0 MB'}
          icon={<HelpCircle className="h-4 w-4 text-blue-500" />}
          isLoading={summaryQuery.isLoading}
          valueClassName="text-blue-600"
        />
        <SimSummaryCard
          title="Monthly Cost"
          value={summary?.monthly_cost ? `$${parseFloat(summary.monthly_cost).toLocaleString('en-CA', { minimumFractionDigits: 2 })}` : '$0.00'}
          icon={<DollarSign className="h-4 w-4 text-emerald-500" />}
          isLoading={summaryQuery.isLoading}
          valueClassName="text-emerald-600"
          subtitle={summary?.volume_tier
            ? `${summary.volume_tier.replace('tier', 'Tier ')}${summary.unpriced_sim_count > 0 ? ` (${summary.unpriced_sim_count} unpriced)` : ''}`
            : undefined}
        />
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-4">
        <div className="relative flex-1 min-w-[200px] max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search ICCID, MSISDN, IMEI, customer..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
          />
        </div>

        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="w-[150px]">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Statuses</SelectItem>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="inactive">Inactive</SelectItem>
            <SelectItem value="suspended">Suspended</SelectItem>
            <SelectItem value="inventory">Inventory</SelectItem>
            <SelectItem value="test">Test</SelectItem>
            <SelectItem value="retired">Retired</SelectItem>
            <SelectItem value="unknown">Unknown</SelectItem>
          </SelectContent>
        </Select>

        <div className="w-[260px]">
          <AccountFilterCombobox
            value={selectedAccountId}
            onChange={handleAccountChange}
            placeholder="All Accounts"
          />
        </div>

        <Button
          variant={showUnassigned ? 'secondary' : 'outline'}
          size="sm"
          onClick={toggleUnassigned}
        >
          <Users className="h-4 w-4 mr-2" />
          Unassigned Only
        </Button>

        <div className="flex items-center gap-1 border rounded-md p-0.5">
          <Button
            variant={matchFilter === 'all' ? 'secondary' : 'ghost'}
            size="sm"
            className="h-7"
            onClick={() => setMatchFilter('all')}
          >
            All
          </Button>
          <Button
            variant={matchFilter === 'matched' ? 'secondary' : 'ghost'}
            size="sm"
            className="h-7"
            onClick={() => setMatchFilter('matched')}
          >
            <Link2 className="h-3.5 w-3.5 mr-1" />
            Matched
          </Button>
          <Button
            variant={matchFilter === 'unmatched' ? 'secondary' : 'ghost'}
            size="sm"
            className="h-7"
            onClick={() => setMatchFilter('unmatched')}
          >
            <Unlink className="h-3.5 w-3.5 mr-1" />
            Unmatched
          </Button>
        </div>

        <span className="text-sm text-muted-foreground whitespace-nowrap ml-auto">
          {total} SIM{total !== 1 ? 's' : ''} found
        </span>
      </div>

      {/* Table */}
      {isLoading ? (
        <Card>
          <CardContent className="p-0">
            <div className="space-y-2 p-4">
              {[...Array(5)].map((_, i) => (
                <Skeleton key={i} className="h-12 w-full" />
              ))}
            </div>
          </CardContent>
        </Card>
      ) : error ? (
        <Card>
          <CardContent className="py-8 text-center">
            <p className="text-red-500">
              Error loading SIM cards. Please try again.
            </p>
            <Button variant="outline" className="mt-4" onClick={handleRefresh}>
              Retry
            </Button>
          </CardContent>
        </Card>
      ) : simCards.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <p className="text-muted-foreground mb-4">
              {search || status !== 'all' || selectedAccountId !== ALL_ACCOUNTS_VALUE || showUnassigned || matchFilter !== 'all'
                ? 'No SIM cards found matching your filters.'
                : 'No SIM cards synced yet. The jasper_sync service will sync data hourly.'}
            </p>
          </CardContent>
        </Card>
      ) : (
        <>
          <SimInventoryTable
            simCards={simCards}
            sortKey={sortKey}
            sortDirection={sortDirection}
            onSort={handleSort}
            volumeTier={summary?.volume_tier}
            onRowClick={setSelectedSimId}
          />

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between pt-2">
              <p className="text-sm text-muted-foreground">
                Page {page + 1} of {totalPages}
              </p>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage((p) => Math.max(0, p - 1))}
                  disabled={page === 0}
                >
                  Previous
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
                  disabled={page >= totalPages - 1}
                >
                  Next
                </Button>
              </div>
            </div>
          )}

          {/* Detail Drawer */}
          <SimDetailSheet
            simCardId={selectedSimId}
            open={!!selectedSimId}
            onOpenChange={(open) => { if (!open) setSelectedSimId(null); }}
            volumeTier={summary?.volume_tier}
          />
        </>
      )}
    </div>
  );
}
